import React, { useState, useCallback, memo } from 'react';
import {
  Box,
  IconButton,
  Collapse,
  Paper,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ApiEndpoint } from '../../types';
import IntelligentFieldDisplay from './IntelligentFieldDisplay';
import { getEntityColor } from '../../utilities/dataItemUtils';
import { getDataItemPrimaryImage } from '../../utilities/dataSourceAdapters';
import { useConfig } from '../../contexts/ConfigContext';

interface AdditionalDetailsPanelProps {
  /** The data item to display additional details for */
  item: any;
  /** API endpoint configuration for data source awareness */
  endpoint?: ApiEndpoint;
  /** Initial expanded state - if not provided, uses smart auto-expansion */
  defaultExpanded?: boolean;
  /** Callback when expand/collapse state changes */
  onExpandChange?: (expanded: boolean) => void;
  /** Custom styling overrides */
  sx?: any;
}

/**
 * Additional Details Panel Component
 * 
 * A reusable, self-contained component that displays additional item details
 * with smooth expand/collapse functionality. Follows React best practices:
 * - Single responsibility
 * - Memoized for performance
 * - Proper state management
 * - Clean styling separation
 * - Accessible interactions
 */
/**
 * Smart auto-expansion logic: determine if Additional Details should be expanded by default
 * Expands when item lacks visual content to provide more information
 * Controlled by config.autoExpandAdditionalDetailsWhenNoImage
 */
const shouldAutoExpand = (item: any, endpoint?: ApiEndpoint, autoExpandEnabled?: boolean): boolean => {
  // Check if auto-expansion is enabled in config (defaults to false if not specified)
  if (!autoExpandEnabled) {
    return false;
  }
  
  // Check if item has a primary image or thumbnail
  const primaryImage = getDataItemPrimaryImage(item);
  const hasThumbnail = item?.thumbnail || item?.entity_thumbnail;
  
  // Auto-expand if no visual content is available
  return !primaryImage && !hasThumbnail;
};

const AdditionalDetailsPanel: React.FC<AdditionalDetailsPanelProps> = memo(({
  item,
  endpoint,
  defaultExpanded,
  onExpandChange,
  sx = {}
}) => {
  const theme = useTheme();
  const config = useConfig();
  
  // Use smart auto-expansion if defaultExpanded not explicitly provided
  const initialExpanded = defaultExpanded !== undefined 
    ? defaultExpanded 
    : shouldAutoExpand(item, endpoint, config.autoExpandAdditionalDetailsWhenNoImage);
    
  const [isExpanded, setIsExpanded] = useState(initialExpanded);

  const handleToggle = useCallback(() => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    onExpandChange?.(newExpanded);
  }, [isExpanded, onExpandChange]);

  // Don't render if no item
  if (!item) return null;

  return (
    <Box 
      sx={{
        mt: 3,
        ...sx
      }}
    >
      {/* Enhanced Toggle Button with Label */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          mb: 2,
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          borderRadius: 2,
          py: 1,
          px: 2,
          '&:hover': {
            backgroundColor: alpha(theme.palette.action.hover, 0.04),
          }
        }}
        onClick={handleToggle}
      >
        <InfoOutlinedIcon 
          sx={{ 
            fontSize: 20, 
            color: 'text.secondary', 
            mr: 1,
            transition: 'color 0.2s ease-in-out'
          }} 
        />
        <Typography 
          variant="subtitle2" 
          sx={{ 
            color: 'text.secondary',
            fontWeight: 500,
            mr: 1,
            userSelect: 'none',
            transition: 'color 0.2s ease-in-out'
          }}
        >
          Additional Details
        </Typography>
        <IconButton
          size="small"
          aria-label={isExpanded ? 'Hide additional details' : 'Show additional details'}
          sx={{ 
            color: 'text.secondary',
            transition: theme.transitions.create(['color', 'background-color', 'transform'], {
              duration: theme.transitions.duration.short,
            }),
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.hover, 0.08),
              color: 'primary.main',
            }
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      {/* Enhanced Collapsible Content */}
      <Collapse 
        in={isExpanded} 
        timeout={{
          enter: theme.transitions.duration.enteringScreen,
          exit: theme.transitions.duration.leavingScreen,
        }}
        unmountOnExit // Performance: unmount when collapsed
      >
        <Paper
          elevation={0}
          sx={{
            // Modern clean design
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
            borderRadius: 3, // More rounded corners
            overflow: 'hidden',
            
            // Enhanced shadow for depth
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)',
            
            // Smooth transitions
            transition: theme.transitions.create([
              'background-color', 
              'border-color', 
              'box-shadow',
              'transform'
            ], {
              duration: theme.transitions.duration.standard,
            }),
            
            // Subtle entrance animation
            animation: isExpanded ? 'slideInUp 0.3s ease-out' : undefined,
            '@keyframes slideInUp': {
              '0%': {
                opacity: 0,
                transform: 'translateY(10px)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0)',
              }
            }
          }}
        >
          {/* Content area with enhanced padding */}
          <Box sx={{ p: 3 }}>
            <IntelligentFieldDisplay 
              item={item} 
              endpoint={endpoint}
              mode="full"
            />
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
});

AdditionalDetailsPanel.displayName = 'AdditionalDetailsPanel';

export default AdditionalDetailsPanel;